import { Box, Flex, useBreakpointValue } from '@chakra-ui/react'
import { omit } from 'lodash-es'
import { useSearchParams } from 'next/navigation'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import logger from '@/utils/logger'
import parseQueryNumber from '@/utils/parseQueryNumber'

import { useAppContext } from '@/client/context/app'
import { HasuraContextWrapper } from '@/client/context/hasura'
import { useUser } from '@/client/context/user'
import RPC from '@/client/rpc'
import { QUERY_POST_ID } from '@/client/services/url'
import { isUserDefined, isUserOnboarding } from '@/client/services/user'
import { UPDATE_LAST_VIEWED_AT_DELAY_MS } from '@/client/services/worlds/notifications/constants'

import LiveHelpModal from '@/components/Community/LiveHelp/LiveHelpModal'
import GlobalModal from '@/components/GlobalModal/GlobalModal'
import GlobalToast from '@/components/GlobalToast/GlobalToast'
import { PlatformLayoutContext } from '@/components/Layout/context'
import ExpandedPostDrawer from '@/components/Layout/ExpandedPostDrawer'
import NavbarBanner from '@/components/Layout/Navbar/NavbarBanner'
import { navBannerHeightDesktop, navBannerHeightMobile } from '@/components/Layout/Navbar/styled'
import NavbarDesktop from '@/components/Layout/NavbarDesktop'
import NavbarMobile from '@/components/Layout/NavbarMobile'
import type { NavigationItemSlug } from '@/components/Layout/types'
import UndisplayedAchievementToasts from '@/components/Layout/UndisplayedAchievementToasts'
import { GlobalContext } from '@/components/Worlds/context'
import useOnlineUsers from '@/components/Worlds/hooks/user/useOnlineUsers'
import NotificationDrawer from '@/components/Worlds/Notification/NotificationDrawer'

function GlobalContextWrapperClient({ children }: { children: ReactNode }): JSX.Element {
  const { onlineUsers } = useOnlineUsers()
  const contextValue = useMemo(() => ({ onlineUsers }), [onlineUsers])

  return <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>
}

function GlobalContextWrapper({ children }: { children: ReactNode }): JSX.Element {
  const appContext = useAppContext()
  const prepassContextValue = useMemo(() => ({ onlineUsers: [] }), [])

  if (appContext.prepass) {
    return <GlobalContext.Provider value={prepassContextValue}>{children}</GlobalContext.Provider>
  }

  return (
    <HasuraContextWrapper LoadingComponent={() => null}>
      <GlobalContextWrapperClient>{children}</GlobalContextWrapperClient>
    </HasuraContextWrapper>
  )
}

export type PlatformLayoutProps = {
  children: React.ReactNode
  hideGlobalModal?: boolean
  activeNavItem?: NavigationItemSlug
  header?: JSX.Element | null
  isContentFixedHeight?: boolean // Whether the contents should take up the available space on the page and no more
}

export default function PlatformLayout({
  children,
  hideGlobalModal = false,
  activeNavItem,
  header,
  isContentFixedHeight = false,
}: PlatformLayoutProps) {
  const user = useUser()
  const searchParams = useSearchParams()

  const postIdQueryParam = parseQueryNumber({
    value: `${searchParams?.get(QUERY_POST_ID)}`,
    defaultValue: 0,
  })
  const initialExpandedPostId = postIdQueryParam > 0 ? postIdQueryParam : null

  const [expandedPostId, setExpandedPostId] = useState<number | null>(initialExpandedPostId)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showingBanner, setShowingBanner] = useState(false)

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const bannerHeight = showingBanner
    ? isMobile
      ? navBannerHeightMobile
      : navBannerHeightDesktop
    : '0px'

  // Keep local state in sync with URL changes (e.g., clicking notification links)
  useEffect(() => {
    setExpandedPostId(initialExpandedPostId)
  }, [initialExpandedPostId])

  const toggleExpandPostId = useCallback(
    (postId: number) => {
      const toggleToPostId = expandedPostId === postId ? null : postId
      setExpandedPostId(toggleToPostId)

      const url = new URL(globalThis.location.href)
      const basePath = `${url.origin}${url.pathname}`

      const originalParams = Object.fromEntries(url.searchParams)

      const updatedParams = new URLSearchParams({
        ...omit(originalParams, QUERY_POST_ID),
        ...(toggleToPostId == null ? {} : { [QUERY_POST_ID]: `${toggleToPostId}` }),
      })

      const newPath = `${basePath}?${updatedParams.toString()}`

      // Using router.push causes everything to rerender and queries to be
      // rerun. Using window stops that from happening.  More context:
      // https://github.com/vercel/next.js/discussions/18072
      globalThis.history.replaceState(
        { ...globalThis.history.state, as: newPath, url: newPath },
        '',
        newPath
      )
    },
    [expandedPostId]
  )

  const layoutContext = useMemo(
    () => ({
      toggleExpandPostId,
      expandedPostId,
      toggleShowNotifications: () => {
        setShowNotifications(show => !show)

        // Wait to update last viewed at so visual effects persist for a little bit
        setTimeout(() => {
          RPC.updateNotificationsLastViewedAt({}, { disableProgressBar: true }).catch(error => {
            logger.error('Error updating notifications last viewed at', {
              error,
            })
          })
        }, UPDATE_LAST_VIEWED_AT_DELAY_MS)
      },
      showNotifications,
    }),
    [expandedPostId, showNotifications, toggleExpandPostId]
  )

  return (
    <GlobalContextWrapper>
      <PlatformLayoutContext.Provider value={layoutContext}>
        <Flex
          position="relative"
          direction="row"
          bg="bg.subtle"
          width="100dvw"
          height="100dvh"
          overflow="hidden"
        >
          <Box position="fixed" top={0} left={0} width="100%" zIndex={10}>
            <NavbarBanner setShowBanner={setShowingBanner} showingBanner={showingBanner} />
          </Box>

          <NavbarDesktop
            activeNavItem={activeNavItem}
            isUserInterestedTutor={user.hasTutorProfile}
          />

          <Flex
            as="main"
            id="main"
            overflow="auto"
            direction="column"
            flexGrow={1}
            height="100dvh"
            position="relative"
          >
            <Box width="100dvw" position="fixed" top={bannerHeight} left={0} zIndex={3}>
              {header}
            </Box>
            {header != null ? (
              <Box width="100%" height={`calc(64px + ${bannerHeight})`} flexShrink={0} />
            ) : (
              <Box width="100%" height={bannerHeight} flexShrink={0} />
            )}
            <Flex
              width="100%"
              flexGrow={1}
              flexShrink={isContentFixedHeight ? 1 : 0}
              minH={0}
              direction="column"
              alignItems="stretch"
            >
              {children}
            </Flex>
            <NavbarMobile activeNavItem={activeNavItem} />
          </Flex>
        </Flex>
        <div id="modal" />
        {isUserDefined(user) && !hideGlobalModal && !isUserOnboarding(user) ? (
          <>
            <GlobalModal />
            <GlobalToast />
          </>
        ) : null}

        <HasuraContextWrapper LoadingComponent={() => null}>
          <LiveHelpModal />
        </HasuraContextWrapper>

        <HasuraContextWrapper LoadingComponent={() => null}>
          <NotificationDrawer
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
          />
        </HasuraContextWrapper>

        <HasuraContextWrapper LoadingComponent={() => null}>
          {expandedPostId == null || expandedPostId === 0 ? null : (
            <ExpandedPostDrawer
              postId={expandedPostId}
              onClose={() => toggleExpandPostId(expandedPostId)}
            />
          )}
        </HasuraContextWrapper>

        <HasuraContextWrapper LoadingComponent={() => null}>
          <UndisplayedAchievementToasts />
        </HasuraContextWrapper>
      </PlatformLayoutContext.Provider>
    </GlobalContextWrapper>
  )
}
